import "@emotion/react";
import { AccentColours, IShoothillTheme } from "./IShoothillTheme";
import { initializeIcons } from "@fluentui/react";
import chroma from "chroma-js";
import { Logger } from "../index";

export const pxToRem = (value: number) => {
    return `${value / 16}rem`;
};
export const adobeXDToRem = (fontSize: number, letterSpacing: number): string => {
    return pxToRem((letterSpacing / 1000) * fontSize) + "rem";
};

//Enable FluentUI Icon support
initializeIcons();

//Define your custom breakpoints here or accept the default values which are the standard common breakpoints
let customBreakpointValues = {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
};

const ThemeColours = (colour: string, text: string, scale = 1): AccentColours => {
    const retval = {
        lighter: chroma(colour)
            .brighten(scale * 2)
            .hex(),
        light: chroma(colour).brighten(scale).hex(),
        main: colour,
        dark: chroma(colour).darken(scale).hex(),
        darker: chroma(colour)
            .darken(scale * 2)
            .hex(),
        text: text,
    };
    /*if (chroma.contrast(retval.main, retval.text) < 4.5) {
        console.warn("Text may not be readable with this colour");
    }*/
    return retval;
};

export const StyleSheet1: IShoothillTheme = {
    // Spacing in pixels.
    space: [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50],

    // Sizes. Keep numbers divisible by 2.
    sizes: {
        larger: pxToRem(40),
        large: pxToRem(30),
        medium: pxToRem(26),
        small: pxToRem(20),
    },
    dates: {
        default: "DD/MM/YYYY",
        short: "DD/MM",
        long: "MMMM Do, YYYY",
    },
    fontStyles: {
        h1: {
            fontSize: pxToRem(30),
            fontWeight: 600,
            letterSpacing: adobeXDToRem(30, -15),
            lineHeight: pxToRem(40),
        },
        h2: {
            fontSize: pxToRem(24),
            fontWeight: 600,
            letterSpacing: adobeXDToRem(24, -15),
            lineHeight: pxToRem(30),
        },
        h3: {
            fontSize: pxToRem(18),
            fontWeight: 600,
            letterSpacing: "0rem",
            lineHeight: pxToRem(18),
        },
        h4: {
            fontSize: pxToRem(14),
            fontWeight: 400,
            letterSpacing: "0rem",
            lineHeight: pxToRem(14),
        },
        h5: {
            fontSize: pxToRem(12),
            fontWeight: 600,
            letterSpacing: "0rem",
            lineHeight: pxToRem(12),
        },
        h6: {
            fontSize: pxToRem(10),
            letterSpacing: "0rem",
            lineHeight: pxToRem(10),
            textTransform: "uppercase",
        },
        h7: {
            fontSize: pxToRem(10),
            fontWeight: 600,
            letterSpacing: "0rem",
            lineHeight: pxToRem(10),
        },
        p: {
            fontSize: pxToRem(14),
            letterSpacing: "0rem",
            lineHeight: pxToRem(14),
        },
        button: {
            fontSize: pxToRem(14),
            letterSpacing: pxToRem(0.3),
            textTransform: "none",
        },
        error: {
            fontSize: pxToRem(10),
            letterSpacing: pxToRem(-0.15),
        },
        field: {
            fontSize: pxToRem(12),
            letterSpacing: pxToRem(-0.18),
        },
        label: {
            fontSize: pxToRem(14),
            letterSpacing: pxToRem(-0.18),
            fontWeight: 600,
        },
        tableHeader: {
            fontSize: pxToRem(14),
            fontWeight: 700,
            letterSpacing: pxToRem(-0.18),
            lineHeight: "42px",
        },
        checkboxLabel: {
            fontSize: pxToRem(12),
            letterSpacing: pxToRem(0.36),
        },
        radioButtonLabel: {
            fontSize: pxToRem(14),
            letterSpacing: pxToRem(0.36),
        },
        headerOptionLabel: {
            fontSize: pxToRem(12),
            letterSpacing: pxToRem(0.6),
        },
    },
    palette: {
        // Use this for your default text.
        common: {
            transparent: "transparent",
            white: "#fff",
            black: "#000",
            // FluentUI focus color. Do not edit!
            focus: "#0078d4",
            // Off-black.
            default: "#fff",
            // Mid blue.
            primary: "#2d91ff",
            secondary: "#B88E00",
            blue: "#1cb6ee",
            // Mid orange.
            // Mid red.
            tertiary: "#DC3545",
            // Mid green.
            quarternary: "#23A73D",
            error: "#c52f3e",
            success: "#23A73D",
            green: "#50AE41",
            purple: "#6679C0",
            lightBlue: "#00b9ee",
            darkPurple: "#25396F",
        },

        // Use these for your controls - those that have a visual state - focused, hover, pressed etc.
        // Light defines the default state with main and dark being non-default states.

        // Light grey background with white text
        // disabled: { light: "#f3f2f1", main: "#f3f2f1", dark: "#f3f2f1", contrastText: "#a19f9d", text: "#a19f9d" },
        disabled: ThemeColours("#f3f2f1", "#a19f9d"),
        // Dark grey background with white text
        default: ThemeColours("#707070", "#fff"),
        // Mid blue background with white text
        primary: ThemeColours("#efefef", "#000"),
        // Mid orange background with white text
        secondary: ThemeColours("#B88E00", "#fff"),
        // Mid red background with white text
        tertiary: ThemeColours("#c52f3e", "#fff"),
        // Mid green background with white text
        quarternary: ThemeColours("#1f9537", "#fff"),
        blue: ThemeColours("#1cb6ee", "#fff"),
        // Dark transparent background with off-black text
        defaultTr: ThemeColours("#f3f2f1", "#171716"),
        // Light grey border with dark grey for hover/focus and off-black text.
        field: ThemeColours("#4b4b4b", "#4a4a4a"),
        tableHeader: ThemeColours("#4b4b4b", "#191919"),
        // Mid red background with mid red text
        error: ThemeColours("#c52f3e", "#fff"),
        warning: ThemeColours("#ff9800", "#fff"),
        info: ThemeColours("#2196f3", "#fff"),
        success: ThemeColours("#4caf50", "#fff"),
        hint: ThemeColours("#1b1b1b", "#000"),
        delete: ThemeColours("#c52f3e", "#fff"),
        //You can still define your colours manually if you want to
        purpleAccent: { lighter: "rgb(198, 51, 255)", light: "rgb(198, 51, 255)", main: "#b800ff", dark: "rgb(128, 0, 178)", darker: "rgb(128, 0, 178)", text: "pink" }, //This is a custom colour
    },

    // Fonts and Text.
    defaultFontStyle: {
        fontFamily: `'Lato', sans-serif`,
        fontSize: pxToRem(16),
        fontWeight: 400,
        letterSpacing: "normal",
        lineHeight: "1.5",
        textTransform: "none",
    },
};
